import { Controller } from "@hotwired/stimulus"
import { TurboBeforeFetchRequestEvent } from "@hotwired/turbo"

import { type DispatchEvent } from "@/entry_points/types"
import { isTopmostElement } from "@/helpers/utils"
import { isMobile } from "@/helpers/window_size"

export default class ModalController extends Controller<HTMLDivElement> {
  static targets = ["container"]
  declare readonly containerTarget: HTMLDivElement

  static readonly FADE_IN_DURATION = 100 // See `--fade-in-duration`
  private static readonly KEYDOWN_EVENT = "keydown"
  private static readonly INITIAL_CLASS = "--initial"
  private static readonly NO_STYLE_CLASS = "no-style"
  private static readonly OPEN_CLASS = "--modal-open"
  private static readonly SMOOTH_SCROLL_CLASS = "scroll-smooth"

  private shouldAnimate = true
  private scrollYPosition = 0

  initialize(): void {
    this.popStateHandler = this.popStateHandler.bind(this)
    this.beforeCache = this.beforeCache.bind(this)
    this.preventTurboHashChange = this.preventTurboHashChange.bind(this)
  }

  connect(): void {
    if (this.isNavigationalModal) {
      window.addEventListener("popstate", this.popStateHandler)
      window.addEventListener("pagehide", this.beforeCache)
      window.addEventListener("turbo:before-fetch-request", this.preventTurboHashChange)

      this.syncWithHistory()
    }
  }

  disconnect(): void {
    if (this.isNavigationalModal) {
      window.removeEventListener("popstate", this.popStateHandler)
      window.removeEventListener("pagehide", this.beforeCache)
      window.removeEventListener("turbo:before-fetch-request", this.preventTurboHashChange)
    }
  }

  animationOff(): void {
    this.shouldAnimate = false
  }

  animationOn(): void {
    this.shouldAnimate = true
  }

  backgroundClose(e: Event): void {
    // Modal spans entire viewport when open, so checking background click means comparing
    // click target to modal itself.
    if (e.target === this.element) {
      this.close()
    }
  }

  close(e?: KeyboardEvent): void {
    if (e && e.type === ModalController.KEYDOWN_EVENT && e.key === undefined) {
      // this ignores artifical keydown events, such as those
      // sent when using browser auto-fill
      return
    }

    if (this.element.hasAttribute("hidden")) {
      return
    }

    if (this.shouldNavModalBeOpen) {
      history.back()

      return
    }

    this.performClose()
  }

  get containerId(): string {
    return this.containerTarget.id
  }

  open(event?: DispatchEvent<{ modalId?: string }>): void {
    if (event?.detail.modalId && event.detail.modalId !== this.containerId) {
      return
    }

    if (!this.element.hasAttribute("hidden")) {
      return
    }

    this.animationOn()
    this.saveScrollPosition()
    this.setAnimationClass()
    this.performOpen()

    if (this.isNavigationalModal) {
      const { history, location } = window
      const currentState = (history.state || {}) as Record<string, unknown>
      const urlWithHash = `${location.pathname}${location.search}#${this.containerId}`

      history.pushState(
        {
          ...currentState,
          modalOpen: true,
          modalId: this.containerId,
          previousHash: location.hash
        },
        "",
        urlWithHash
      )
    }
  }

  useModalView(): void {
    this.element.classList.remove(ModalController.NO_STYLE_CLASS)
  }

  useNoStyleView(): void {
    document.body.classList.remove(ModalController.OPEN_CLASS)
    this.element.classList.add(ModalController.NO_STYLE_CLASS)
    this.element.removeAttribute("hidden")
  }

  private beforeCache = (): void => {
    // If the modal is a part of browser history
    // it disables animation to prevent the modal from being restored with a stale transform state (cropped slideout)
    this.animationOff()
    this.setAnimationClass()
  }

  private syncWithHistory(): void {
    const shouldOpen = this.shouldNavModalBeOpen
    const isOpen = !this.element.hasAttribute("hidden")

    if (shouldOpen !== isOpen) {
      this.animationOff()

      if (shouldOpen) {
        this.performOpen()
      } else {
        this.performClose()
      }
    }
  }

  private focusInModal = (): void => {
    // To preserve focus within modal (e.g. from tabbing), check if the active element is outside
    // the modal and if so, re-focus the container.
    if (isTopmostElement(this.element) && !this.element.contains(document.activeElement)) {
      this.containerTarget.focus()
    }
  }

  private restoreScrollPosition(): void {
    // Turn off smooth scrolling
    document.documentElement.classList.remove(ModalController.SMOOTH_SCROLL_CLASS)

    // Restore position before modal opened
    window.scrollTo({ top: this.scrollYPosition })

    // Turn smooth scrolling back on
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add(ModalController.SMOOTH_SCROLL_CLASS)
    })
  }

  private popStateHandler(): void {
    if (this.shouldNavModalBeOpen) {
      if (this.element.hasAttribute("hidden")) {
        window.requestAnimationFrame(() => {
          this.performOpen()
        })
      }

      return
    }

    if (this.element && !this.element.hasAttribute("hidden")) {
      this.animationOff()
      this.performClose()
    }
  }

  private preventTurboHashChange = (e: TurboBeforeFetchRequestEvent): void => {
    const currentUrl = new URL(window.location.href)
    const requestUrl = new URL(e.detail.url, window.location.href)

    if (currentUrl.pathname === requestUrl.pathname && currentUrl.search === requestUrl.search) {
      e.preventDefault()
    }
  }

  protected performOpen(): void {
    document.body.classList.add(ModalController.OPEN_CLASS)
    document.addEventListener("focus", this.focusInModal, true)
    this.element.removeAttribute("hidden")

    // Focus modal on open (this also ensures `keydown` handler works without user interaction).
    // Elements with CSS `visibility: hidden` aren't focusable, so wait for animation.
    window.setTimeout(() => {
      this.containerTarget.focus()
    }, ModalController.FADE_IN_DURATION)

    document.dispatchEvent(new Event(`${this.identifier}:open`))
  }

  private performClose(): void {
    this.setAnimationClass()
    document.body.classList.remove(ModalController.OPEN_CLASS)
    document.removeEventListener("focus", this.focusInModal, true)
    this.element.setAttribute("hidden", "until-found")
    this.restoreScrollPosition()

    this.dispatch("closeModal")
  }

  private saveScrollPosition(): void {
    // CSS applies `position: fixed` when modal opens because iOS reasons. Doing so means we lose
    // page scroll position, so keep track of it to restore it on modal close.
    this.scrollYPosition = Math.max(0, window.scrollY)
    document.body.style.setProperty("--scroll-y-position", `${this.scrollYPosition}px`)
  }

  private setAnimationClass(): void {
    this.element.classList.toggle(ModalController.INITIAL_CLASS, !this.shouldAnimate)
  }

  private get isNavigationalModal(): boolean {
    return this.element.classList.contains("reveal:slide-left") && isMobile()
  }

  private get shouldNavModalBeOpen(): boolean {
    const historyState = history.state as { modalOpen?: boolean; modalId?: string } | null
    return (
      Boolean(historyState?.modalOpen && historyState.modalId === this.containerId) ||
      (this.isNavigationalModal && location.hash === `#${this.containerId}`)
    )
  }
}
